/**
 * Shared 12-point connection handles for structured nodes.
 *
 * Edges are routed by `autoAssignHandles` (parser), which assigns handle ids
 * following the 12-handle convention `${side}-${slot}-${source|target}` —
 * e.g. `right-top-source`, `left-top-target`. React Flow SILENTLY DROPS any
 * edge whose `sourceHandle`/`targetHandle` id does not exist on the node, so a
 * node MUST expose every id in this convention or its edges vanish.
 *
 * IconLibraryNode and APIResourceNode declare these inline; StateNode / C4Node /
 * UMLClassNode use this shared component so they can never drift out of the
 * 12-handle contract again. (Root cause of the "MCP diagram shows nodes but no
 * edges" bug: StateNode/C4Node only exposed the 4 center handles.)
 */

import { Handle, Position } from '@xyflow/react'

const SLOTS = ['top', 'center', 'bottom'] as const // for left/right (vertical slots)
const HSLOTS = ['left', 'center', 'right'] as const // for top/bottom (horizontal slots)

const pct = (slot: string) => (slot === 'top' || slot === 'left' ? '25%' : slot === 'bottom' || slot === 'right' ? '75%' : '50%')

// Keep handles unobtrusive but present (hit-area only; structured nodes don't
// show the dotted handles the icon node does).
const handleStyle: React.CSSProperties = { width: 6, height: 6, background: 'transparent', border: 'none', minWidth: 0, minHeight: 0 }

export function NodeConnectionHandles() {
  return (
    <>
      {/* Top + Bottom: horizontal slots (left/center/right) */}
      {HSLOTS.map((slot) => (
        <>
          <Handle key={`t-${slot}-s`} type="source" position={Position.Top} id={`top-${slot}-source`} style={{ ...handleStyle, left: pct(slot) }} />
          <Handle key={`t-${slot}-t`} type="target" position={Position.Top} id={`top-${slot}-target`} style={{ ...handleStyle, left: pct(slot) }} />
          <Handle key={`b-${slot}-s`} type="source" position={Position.Bottom} id={`bottom-${slot}-source`} style={{ ...handleStyle, left: pct(slot) }} />
          <Handle key={`b-${slot}-t`} type="target" position={Position.Bottom} id={`bottom-${slot}-target`} style={{ ...handleStyle, left: pct(slot) }} />
        </>
      ))}
      {/* Left + Right: vertical slots (top/center/bottom) */}
      {SLOTS.map((slot) => (
        <>
          <Handle key={`r-${slot}-s`} type="source" position={Position.Right} id={`right-${slot}-source`} style={{ ...handleStyle, top: pct(slot) }} />
          <Handle key={`r-${slot}-t`} type="target" position={Position.Right} id={`right-${slot}-target`} style={{ ...handleStyle, top: pct(slot) }} />
          <Handle key={`l-${slot}-s`} type="source" position={Position.Left} id={`left-${slot}-source`} style={{ ...handleStyle, top: pct(slot) }} />
          <Handle key={`l-${slot}-t`} type="target" position={Position.Left} id={`left-${slot}-target`} style={{ ...handleStyle, top: pct(slot) }} />
        </>
      ))}
    </>
  )
}

/** The full set of handle ids this component renders — for regression tests. */
export const STRUCTURED_NODE_HANDLE_IDS: string[] = (() => {
  const ids: string[] = []
  for (const slot of ['left', 'center', 'right']) for (const side of ['top', 'bottom']) for (const st of ['source', 'target']) ids.push(`${side}-${slot}-${st}`)
  for (const slot of ['top', 'center', 'bottom']) for (const side of ['left', 'right']) for (const st of ['source', 'target']) ids.push(`${side}-${slot}-${st}`)
  return ids
})()
